import { faMinusCircle } from '@fortawesome/pro-regular-svg-icons/faMinusCircle';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle';
import { MediaQuery } from '@maxpreps/shared/styles/breakpoint';
import Font from '@maxpreps/shared/styles/font';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Anchor from '../../corecomponents/Anchor/Anchor';
import useIsAdmin from '../../hooks/useIsAdmin';
import THEME from '../../styles/colors';
import Icon, { IconType } from '../Icon/Icon';
import StyledAnchorAsButton from '../StyledAnchor/StyledAnchorAsButton';
import StyledText from '../Text/StyledText';

const StyledExpandableList = styled.ul`
  color: ${THEME.text.tertiary};
  padding-top: 4px;
  & li {
    margin-top: 8px;
    & :first-child {
      margin-top: 4px;
    }
    > a,
    > ${StyledAnchorAsButton}, > ${StyledText} {
      display: flex;
      align-items: center;
    }
  }
  & svg {
    margin-right: 6px;
    max-width: 16px;
  }
  & a {
    display: flex;
    align-items: center;
    /* For onClick anchors - TODO refactor */
    cursor: pointer;
  }
  & .notes {
    white-space: pre-wrap;
  }
`;

const StyledHorizontalList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: baseline;
  gap: 16px;

  ${MediaQuery.Tablet} {
    flex-direction: row;
  }
`;

export interface IListItemProps {
  key: string;
  text?: string;
  iconType?: IconType;
  icon?: any;
  iconColor?: string;
  href?: string;
  onClick?: Function;
  adminOnly?: boolean;
  newTab?: boolean;
  className?: string;
  cols?: IListItemProps[];
  isButton?: boolean;
}

const ListItem: React.FC<IListItemProps> = ({
  text,
  iconType,
  icon,
  iconColor,
  href,
  onClick,
  adminOnly,
  newTab,
  className,
  cols,
  isButton
}) => {
  const isAdmin = useIsAdmin();
  if (adminOnly && !isAdmin) return null;

  // recurisvely make sub-ListItems
  if (cols) {
    return (
      <li>
        <StyledHorizontalList>
          {cols.map(c => (
            <ListItem {...c} key={c.key} />
          ))}
        </StyledHorizontalList>
      </li>
    );
  }

  const iconDisplay = icon && (
    <Icon
      size={16}
      color={iconColor || THEME.text.tertiary}
      iconType={iconType}
      icon={icon}
      fixedWidth
      style={{ verticalAlign: 'bottom' }}
    />
  );

  return (
    <li className={className}>
      {href ? (
        <Anchor
          href={href}
          $color={THEME.other.link}
          target={newTab ? '_blank' : undefined}
        >
          {iconDisplay}
          {text}
        </Anchor>
      ) : isButton ? (
        <StyledAnchorAsButton onClick={onClick as any}>
          {iconDisplay}
          {text}
        </StyledAnchorAsButton>
      ) : (
        <StyledText>
          {iconDisplay}
          {text}
        </StyledText>
      )}
    </li>
  );
};

const COMMON_PROPS = {
  adminOnly: true,
  iconColor: THEME.other.link,
  isButton: true
};

const SHOW_MORE = {
  key: 'showMore',
  icon: faPlusCircle,
  text: 'Show More',
  ...COMMON_PROPS
};

const SHOW_LESS = {
  key: 'showLess',
  icon: faMinusCircle,
  text: 'Show Less',
  ...COMMON_PROPS
};

export interface IExpandableListProps {
  /**
   * Items taht will show in a 'more' dropdown
   */
  listItems?: IListItemProps[];
  /**
   * Items taht will show in the main list
   */
  outerListItems?: IListItemProps[];
  /**
   * Toggle for exanding the 'more' menu if there are items in the listItems prop
   */
  isExpanded?: boolean;
}

const ExpandableList: React.FC<IExpandableListProps> = ({
  listItems = [],
  outerListItems = [],
  isExpanded = false
}) => {
  const isAdmin = useIsAdmin();
  const [showMore, setShowMore] = useState(!isAdmin || isExpanded);

  useEffect(() => {
    setShowMore(isExpanded);
  }, [isExpanded]);

  if (!listItems?.length && !outerListItems.length) return null;

  const outer = outerListItems.map(x => <ListItem {...x} key={x.key} />);
  const inner = listItems.map(x => <ListItem {...x} key={x.key} />);

  return (
    <StyledExpandableList className={Font.f12_tall}>
      {outer}
      {listItems?.length > 0 && (
        <>
          {!showMore && (
            <ListItem {...SHOW_MORE} onClick={() => setShowMore(true)} />
          )}
          {showMore && inner}
          {showMore && (
            <ListItem {...SHOW_LESS} onClick={() => setShowMore(false)} />
          )}
        </>
      )}
    </StyledExpandableList>
  );
};

export default ExpandableList;
